import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as StyledComponents from './StyledComponents';
import { format, addDays, parseISO, addHours, isAfter, isBefore } from 'date-fns';
import { toZonedTime } from 'date-fns-tz'

import Header from './Header/Header';

import { EventData, State, Provider, Category } from '../types';
import EventDateRow from './EventDateRow';
import SkeletonEventRow from './SkeletonEventRow';

const CHILEAN_REGIONS: State[] = [
  { value: 'Arica y Parinacota', label: 'Arica y Parinacota', eventCount: 0 },
  { value: 'Tarapacá', label: 'Tarapacá', eventCount: 0 },
  { value: 'Antofagasta', label: 'Antofagasta', eventCount: 0 },
  { value: 'Atacama', label: 'Atacama', eventCount: 0 },
  { value: 'Coquimbo', label: 'Coquimbo', eventCount: 0 },
  { value: 'Valparaíso', label: 'Valparaíso', eventCount: 0 },
  { value: 'Región Metropolitana', label: 'Región Metropolitana', eventCount: 0 },
  { value: "O'Higgins", label: "O'Higgins", eventCount: 0 },
  { value: 'Maule', label: 'Maule', eventCount: 0 },
  { value: 'Ñuble', label: 'Ñuble', eventCount: 0 },
  { value: 'Bio Bío', label: 'Biobío', eventCount: 0 },
  { value: 'Araucanía', label: 'Araucanía', eventCount: 0 },
  { value: 'Los Ríos', label: 'Los Ríos', eventCount: 0 },
  { value: 'Los Lagos', label: 'Los Lagos', eventCount: 0 },
  { value: 'Aysén', label: 'Aysén', eventCount: 0 },
  { value: 'Magallanes y la Antártica Chilena', label: 'Magallanes', eventCount: 0 }
];

const removeDiacritics = (str: string) => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

const EventDashboard: React.FC = () => {
  
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  const [events, setEvents] = useState<EventData[]>([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedState, setSelectedState] = useState<State | null>(
    CHILEAN_REGIONS.find(region => region.value === 'Región Metropolitana') || null
  );
  const [states, setStates] = useState<State[]>(CHILEAN_REGIONS);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchVisible, setIsSearchVisible] = useState(false);

  const [groupedEvents, setGroupedEvents] = useState<{ [key: string]: EventData[] }>({});

  // Computed loading state that combines both loadings
  const loading = fetchLoading || filterLoading;

  useEffect(() => {
    const fetchEvents = async () => {
      setFetchLoading(true);
      setError(null);
      try {
        const nowInChile = toZonedTime(new Date(), 'America/Santiago');
        const today = format(nowInChile, 'yyyy-MM-dd');
        const endDate = format(addDays(nowInChile, 30), 'yyyy-MM-dd');
        
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/events`, { 
          params: {
            start_date: today,
            end_date: endDate,
          }
        });
        
        // Prepare all the data first
        const eventsData = response.data;
        const updatedStates = CHILEAN_REGIONS.map(region => ({
          ...region,
          eventCount: eventsData.filter((event: EventData) => event.state === region.value).length
        }));
        
        // Then update the selectedState with the correct event count
        const defaultState = updatedStates.find(region => region.value === 'Región Metropolitana') || null;

        // Extract providers from events
        const providerSet: Set<string> = new Set(eventsData.map((event: EventData) => event.source));
        const providerList: Provider[] = Array.from(providerSet)
          .filter((provider): provider is string => provider != null)
          .map((provider) => ({
            value: provider,
            label: provider,
            eventCount: eventsData.filter((event: EventData) => event.source === provider).length
          }));

        // Extract categories from events
        const categorySet: Set<string> = new Set(eventsData.map((event: EventData) => event.category));
        const categoryList: Category[] = Array.from(categorySet)
          .filter((category): category is string => category != null)
          .map((category) => ({
            value: category,
            label: category,
            eventCount: eventsData.filter((event: EventData) => event.category === category).length
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        // Batch all state updates
        setEvents(eventsData);
        setStates(updatedStates);
        setSelectedState(defaultState);
        setProviders(providerList);
        setCategories(categoryList);
        
      } catch (error) {
        setError('Error recuperando eventos. Por favor, inténtelo de nuevo más tarde.');
      } finally {
        setFetchLoading(false);
      }
    };

    fetchEvents();
  }, []);

  useEffect(() => {
    const filterEvents = async () => {
      // Skip filtering if we're still fetching the initial data
      if (fetchLoading) return;

      setFilterLoading(true);
      
      let filtered = events;
      if (selectedState) {
        filtered = filtered.filter(event => event.state === selectedState.value);
      }
      if (selectedProvider) {
        filtered = filtered.filter(event => event.source === selectedProvider.value);
      }
      if (selectedCategory && selectedCategory.value !== null) {
        filtered = filtered.filter(event => event.category === selectedCategory.value);
      }
      if (searchTerm.trim()) {
        const searchLower = removeDiacritics(searchTerm.toLowerCase());
        filtered = filtered.filter(event => 
          removeDiacritics(event.title.toLowerCase()).includes(searchLower) ||
          removeDiacritics(event.location.toLowerCase()).includes(searchLower)
        );
      }
      setGroupedEvents(groupEventsByDate(filtered));
      setFilterLoading(false);
    }
    filterEvents();

  }, [selectedState, selectedProvider, selectedCategory, events, searchTerm, fetchLoading]);

  // Group events by date
  const groupEventsByDate = (events: EventData[]) => {
    const groupedEvents: { [key: string]: EventData[] } = {};
    
    events.forEach(event => {
      const now = new Date();
      // Filter out past datetimes
      const inProgressOrFutureEventDatetimes = event.datetimes
        .filter(dt => {
          const eventStart = parseISO(dt.start);
          const eventEnd = dt.end ? parseISO(dt.end) : null;
          const threeHoursFromNow = addHours(now, 3);
          
          // Exclude events where the end date is more than 7 days after the start date
          if (eventEnd && isAfter(eventEnd, addDays(eventStart, 7))) {
            return false;
          }
          
          return isAfter(eventStart, now) || 
                 (eventEnd && isAfter(eventEnd, threeHoursFromNow));
        })
        .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

      inProgressOrFutureEventDatetimes.forEach(dt => {
        const eventStart = parseISO(dt.start);
        const eventEnd = dt.end ? parseISO(dt.end) : null;
        
        // Use end date for grouping if the event has started and has an end date
        const isInProgress = eventEnd && isBefore(eventStart, now);
        const dateToGroup = isInProgress ? now : eventStart;
        const dateKey = format(dateToGroup, 'yyyy-MM-dd');
        
        // If the dateKey is not already in the groupedEvents, add it
        if (!groupedEvents[dateKey]) {
          groupedEvents[dateKey] = [];
        }
        // If the event is not already in the groupedEvents, add it
        if (!groupedEvents[dateKey].some(e => e.id === event.id)) {
          groupedEvents[dateKey].push({
            ...event,
            datetimes: [dt]
          });
        } else {
          // If the event is already in the groupedEvents, add the datetime to the event
          groupedEvents[dateKey].find(e => e.id === event.id)?.datetimes.push(dt);
        }
      });
    });

    // Sort the keys (dates) of groupedEvents
    const sortedDates = Object.keys(groupedEvents).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

    // Create a new object with sorted keys and sorted events
    // We sort the events by the latest datetime to prioritize the most recent events
    const sortedGroupedEvents: { [key: string]: EventData[] } = {};
    sortedDates.forEach(date => {
      sortedGroupedEvents[date] = groupedEvents[date].sort((a, b) => {
        // First sort by number of datetimes (descending)
        const dateTimeDiff = a.datetimes.length - b.datetimes.length;
        if (dateTimeDiff !== 0) return dateTimeDiff;
        
        // If equal number of datetimes, use the original time-based sorting
        const aTime = new Date(a.datetimes[a.datetimes.length - 1].start).getTime();
        const bTime = new Date(b.datetimes[b.datetimes.length - 1].start).getTime();
        return aTime - bTime;
      });
    });
    console.log('grouped Events:', sortedGroupedEvents);
    return sortedGroupedEvents;
  };

  const handleStateChange = (selectedOption: State | null) => {
    setSelectedState(selectedOption);
  };

  const handleProviderChange = (selectedOption: Provider | null) => {
    setSelectedProvider(selectedOption);
  };

  const handleCategoryChange = (selectedOption: Category | null) => {
    setSelectedCategory(selectedOption);
  };

  // Create date in Santiago timezone
  const now = toZonedTime(new Date(), 'America/Santiago');
  const todayKey = format(now, 'yyyy-MM-dd');
  const tomorrowKey = format(addDays(now, 1), 'yyyy-MM-dd');

  return (
    <>
      <Header
        categories={categories}
        selectedCategory={selectedCategory}
        states={states}
        selectedState={selectedState}
        providers={isDevelopment ? providers : []}
        selectedProvider={isDevelopment ? selectedProvider : null}
        onCategoryChange={handleCategoryChange}
        onStateChange={handleStateChange}
        onProviderChange={handleProviderChange}
        onSearchChange={setSearchTerm}  
        isSearchVisible={isSearchVisible}
        setIsSearchVisible={setIsSearchVisible}
      />
      
      <StyledComponents.Container paddingTop={isSearchVisible ? '193px' : '150px'}>
        <StyledComponents.EventGridContainer>
          {loading ? (
            <>
              <SkeletonEventRow />
              <SkeletonEventRow />
            </>
          ) : error ? (
            <StyledComponents.ErrorMessage>Error: {error}</StyledComponents.ErrorMessage>
          ) : groupedEvents && Object.keys(groupedEvents).length > 0 ? (
            Object.entries(groupedEvents).map(([groupedDate, dateEvents]) => (
              <EventDateRow
                key={groupedDate}
                rowDate={groupedDate}
                dateEvents={dateEvents}
                todayKey={todayKey}
                tomorrowKey={tomorrowKey}
              />
            ))
          ) : (
              <p style={{ textAlign: 'center'}}>No se encontraron eventos</p>
          )}
        </StyledComponents.EventGridContainer>
      </StyledComponents.Container>
    </>
  );
};

export default EventDashboard;
